import { Alert, Button, Card, Form, Input, Typography } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import { useAuthStore } from '@/store/auth';
import { changePassword } from '@/api/auth';

const schema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string().min(8, 'At least 8 characters'),
  confirmPassword: z.string(),
}).refine((d) => d.newPassword === d.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type FormValues = z.infer<typeof schema>;

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const user = useAuthStore((s) => s.user);
  const [serverError, setServerError] = useState<string | null>(null);

  const { control, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      const res = await changePassword(values.currentPassword, values.newPassword);
      if (user) {
        setUser({
          ...user,
          mustChangePassword: res.mustChangePassword,
          permissions: res.permissions,
          scope: res.scope,
        });
      }
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      setServerError((err as { response?: { data?: { detail?: string } } }).response?.data?.detail
        ?? 'Could not change password.');
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <Card style={{ width: 420 }}>
        <Typography.Title level={4} style={{ marginTop: 0 }}>Set a new password</Typography.Title>
        <Typography.Paragraph type="secondary">
          You must change your password before continuing. Pick something at least 8 characters long.
        </Typography.Paragraph>
        {serverError && <Alert type="error" showIcon message={serverError} style={{ marginBottom: 16 }} />}
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <Form.Item label="Current password" validateStatus={errors.currentPassword ? 'error' : ''}
                     help={errors.currentPassword?.message}>
            <Controller name="currentPassword" control={control}
                        render={({ field }) => <Input.Password {...field} autoComplete="current-password" autoFocus />} />
          </Form.Item>
          <Form.Item label="New password" validateStatus={errors.newPassword ? 'error' : ''}
                     help={errors.newPassword?.message}>
            <Controller name="newPassword" control={control}
                        render={({ field }) => <Input.Password {...field} autoComplete="new-password" />} />
          </Form.Item>
          <Form.Item label="Confirm new password" validateStatus={errors.confirmPassword ? 'error' : ''}
                     help={errors.confirmPassword?.message}>
            <Controller name="confirmPassword" control={control}
                        render={({ field }) => <Input.Password {...field} autoComplete="new-password" />} />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={isSubmitting}>Update password</Button>
        </Form>
      </Card>
    </div>
  );
}
